import { useCallback, useContext } from 'react';
import type { PostStampCardResponse } from '~/api/aspida/@types';
import { postStampCard } from '~/api/stampCard';
import { ErrorModal } from '~/components/modals/Error';
import { ModalContext } from '~/context/ModalContext';
import { achievementFilter } from '~/features/stamps/achievements/achievementService';
import { StampType } from '~/features/stamps/interfaces/enums';
import { useRequestApi } from '~/hooks/useRequestApi';
import { userGetters } from '~/recoil/user';
import { useAchievementStore } from '~/stores';
import { wait } from '~/utils/wait';

export const useAchievementStamps = () => {
  const { openModal } = useContext(ModalContext);
  const addAchievement = useAchievementStore((s) => s.addAchievement);
  const user = userGetters.useValue();
  const userId = user.selected_child_id;

  const postStampCardRequest = useRequestApi(postStampCard, (res) => res);
  const postBirthdayStampCardRequest = useRequestApi(
    postStampCard,
    (res) => res,
    () =>
      openModal(() =>
        ErrorModal({
          titleRuby: 'エラー',
          title: 'ERROR!',
          body: `event_txt_errorReceived`,
        }),
      ),
  );

  // postのレスポンスから何のスタンプか取得できないのでstampTypeを返す関数でラップする
  const requestPost = useCallback(
    async (stampTypes: StampType[], childId?: number | undefined): Promise<StampType[]> => {
      const succeedStamps: StampType[] = [];

      for (const stamp of stampTypes) {
        const params = {
          stamp_type: stamp,
          child_id: childId === undefined ? user.selected_child_id : childId,
        };

        let res: PostStampCardResponse | undefined = undefined;

        switch (stamp) {
          case 'birthday':
            res = await postBirthdayStampCardRequest(params);
            break;
          default:
            res = await postStampCardRequest(params);
            break;
        }

        if (res !== undefined) {
          succeedStamps.push(stamp);
        }
      }

      return succeedStamps;
    },
    [user],
  );

  // 計測スタンプ・継続スタンプの有無を取得する
  const postFootMeasurementStamps = useCallback(async (childId?: number | undefined) => {
    // NOTE: 計測スタンプ獲得が登録されてから継続スタンプを取得したいので2秒待っている
    const getFootMeasurementStamp = await requestPost([StampType.FootMeasurement], childId);
    await wait(2000);
    const getExtraBonusStamp = await requestPost([StampType.ExtraBonus], childId);
    const getAchievements = achievementFilter([...getFootMeasurementStamp, ...getExtraBonusStamp]);
    if (!getAchievements) return;
    addAchievement(childId ?? userId, getAchievements);
  }, []);

  // あしあーとスタンプ・あしあーとギャラリー投稿スタンプの有無を取得する
  const postCreateArtworkStamps = useCallback(async () => {
    const requestTypes: StampType[] = [StampType.CreateArtwork, StampType.PublishedGallery];
    await requestPost(requestTypes).then((res) => {
      const getAchievements = achievementFilter(res);
      if (!getAchievements) return;
      addAchievement(userId, getAchievements);
    });
  }, []);

  // 誕生日スタンプの有無を取得する
  const postBirthdayStamps = useCallback(async () => {
    const requestTypes: StampType[] = [StampType.Birthday];
    const res = await requestPost(requestTypes);
    const getAchievements = achievementFilter(res);
    if (!getAchievements) return;
    addAchievement(userId, getAchievements);
  }, []);

  // 靴登録スタンプの有無を取得する
  const postRegisterShoesStamps = useCallback(async () => {
    const requestTypes: StampType[] = [StampType.RegisterShoes];
    await requestPost(requestTypes).then((res) => {
      const getAchievements = achievementFilter(res);
      if (!getAchievements) return;
      addAchievement(userId, getAchievements);
    });
  }, []);

  return {
    postFootMeasurementStamps,
    postCreateArtworkStamps,
    postBirthdayStamps,
    postRegisterShoesStamps,
  };
};
